/**
 * List config for Settings › Warehouses — one list of every warehouse account, each with a status
 * (ACT2-1031), on the shared list stack (`useListControls` + `ListToolbar` + `filterSortItems`) the
 * way Settings › Accounts uses it.
 *
 * A row is either a Lookout CONNECTION (Connected, or Needs attention) or a RudderStack account
 * Lookout is not connected to (Available, or Can't connect). In a sibling workspace (ACT2-1124) the
 * accounts of the organization's other workspaces join the same list as Available rows: Connect
 * copies the account into this workspace, then connects the copy. Display-agnostic and pure, so the
 * statuses, the order and the de-duplication are testable without rendering.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import dayjs from 'dayjs';

import type { FilterGroup } from '@/_shared/components/list/ListToolbar';
import { type ListDeriveConfig, parseMultiFilter, type SortState } from '@/_shared/lib/list-controls';

import { accountRowIdentity } from './adopt/adoptPresentation';
import type { RsAccountOverview } from './adopt/useAdoptAccounts';
import { boundRsAccount, type ConnectionHealth, connectionHealth, ownedByRudderStack } from './connectionHealth';
import type { WarehouseAccount } from './useWarehouseAccounts';
import { canonicalWarehouseTypeKey, warehouseTypeMeta } from './warehouseTypes';

export type WarehouseStatus = 'connected' | 'needs_attention' | 'available' | 'cant_connect';

/** Plain words only: never "paused", "opted in" or "being enabled". */
export const WAREHOUSE_STATUS_LABEL: Record<WarehouseStatus, string> = {
  connected: 'Connected',
  needs_attention: 'Needs attention',
  available: 'Available',
  cant_connect: 'Can’t connect',
};

/** The default order: what works, what is broken, what is one click away, what cannot be used. */
const STATUS_RANK: Record<WarehouseStatus, number> = {
  connected: 0,
  needs_attention: 1,
  available: 2,
  cant_connect: 3,
};

/** Why a RudderStack account can't be connected. Scoped to the account, not the workspace. */
export const CANNOT_CONNECT_REASON =
  'Lookout can’t read this type of account’s credentials from RudderStack yet, so a connection made from it could not run queries.';

interface RowBase {
  /** Unique across both kinds: a connection uuid or a RudderStack account id. */
  id: string;
  status: WarehouseStatus;
  name: string;
  /** Backend type, for the brand icon. */
  type: string;
  typeLabel: string;
  /** Host / database, or a Lookout-minted account's raw name. Null when there is nothing to show. */
  detail: string | null;
}

/**
 * ACT2-1109: the status dot a connection row shows for its background Warehouse orientation run. It
 * tells people why the agent may not have full context on that warehouse yet, and where the finished
 * map lives. Display-agnostic: the cell picks the dot style from `state`.
 */
export type OrientationState = 'orienting' | 'oriented' | 'partial' | 'blocked' | 'not_oriented';

export interface OrientationIndicator {
  state: OrientationState;
  /** The tooltip's lead word and the dot's accessible name. */
  label: string;
  /** The tooltip's sentence: what it means and, where there is one, what to do. */
  description: string;
}

export const ORIENTATION_LABEL: Record<OrientationState, string> = {
  orienting: 'Orienting',
  oriented: 'Oriented',
  partial: 'Partly oriented',
  blocked: 'Couldn’t orient',
  not_oriented: 'Not oriented',
};

const ORIENTING_DESCRIPTION =
  'Lookout is mapping this warehouse in the background. Answers about it may lack context until it finishes.';
const NOT_ORIENTED_DESCRIPTION =
  'The background map of this warehouse didn’t finish, so answers about it may lack context. An admin can choose Run orientation in this connection’s menu, or run Warehouse orientation from a chat.';
/** A server that predates ACT2-1340 sends no `fix`; these are its stand-ins. */
const BLOCKED_DESCRIPTION =
  'Lookout couldn’t read this warehouse, so it has no map of it yet. Test the connection, then choose Run orientation in its menu.';
const PARTIAL_DESCRIPTION =
  'Some parts of this warehouse couldn’t be read, so the map is incomplete. Choose Run orientation in this connection’s menu after fixing access.';

/** ACT2-1340: the server's plain-words fix, as one sentence pair. */
function fixDescription(fix: { message: string; nextStep: string } | null | undefined, fallback: string): string {
  return fix ? `${fix.message} ${fix.nextStep}` : fallback;
}

function orientedDescription(updatedAt: string | null, noteTitle: string | null): string {
  const when = updatedAt ? `Mapped on ${dayjs(updatedAt).format('D MMM YYYY')}.` : 'Mapped in the background.';
  return `${when} See Context Hub › ${noteTitle ?? 'Warehouse orientation'}.`;
}

export function orientationIndicator(orientation: WarehouseAccount['orientation']): OrientationIndicator | null {
  switch (orientation?.status) {
    case 'queued':
    case 'running':
      return { state: 'orienting', label: ORIENTATION_LABEL.orienting, description: ORIENTING_DESCRIPTION };
    case 'done':
      if (orientation.reason === 'partial') {
        return {
          state: 'partial',
          label: ORIENTATION_LABEL.partial,
          description: fixDescription(orientation.fix, PARTIAL_DESCRIPTION),
        };
      }
      return {
        state: 'oriented',
        label: ORIENTATION_LABEL.oriented,
        description: orientedDescription(orientation.updatedAt, orientation.noteTitle),
      };
    case 'blocked':
      return {
        state: 'blocked',
        label: ORIENTATION_LABEL.blocked,
        description: fixDescription(orientation.fix, BLOCKED_DESCRIPTION),
      };
    case 'failed':
    case 'interrupted':
      return { state: 'not_oriented', label: ORIENTATION_LABEL.not_oriented, description: NOT_ORIENTED_DESCRIPTION };
    case undefined:
      return null;
  }
}

export interface ConnectionRow extends RowBase {
  kind: 'connection';
  account: WarehouseAccount;
  health: ConnectionHealth;
  /** ACT2-1109: the background orientation run's dot; null when no run was ever started. */
  orientation: OrientationIndicator | null;
  /** A person connected this account from RudderStack: it offers Detach, not Rotate or Delete. */
  ownedByRudderStack: boolean;
  bound: RsAccountOverview | undefined;
}

export interface RsAccountRow extends RowBase {
  kind: 'rs_account';
  rsAccount: RsAccountOverview;
}

/** A warehouse account of another workspace of the organization, as `accountCopies.listOptions` answers it. */
export type OrganizationAccountOption = Awaited<
  ReturnType<RouterClient<AppRouter>['accountCopies']['listOptions']>
>['accounts'][number];

/** ACT2-1124: an account of another workspace of the organization. Connect copies it into this workspace first. */
export interface OrganizationAccountRow extends RowBase {
  kind: 'org_account';
  option: OrganizationAccountOption;
}

export type WarehouseListRow = ConnectionRow | RsAccountRow | OrganizationAccountRow;

/** `TYPE|host|db` → `host / db`, as the connection row shows its coordinates. */
export function connectionCoordinates(accountKey: string): string {
  const [, host = '', db = ''] = accountKey.split('|');
  return db && db !== host ? `${host} / ${db}` : host;
}

/**
 * Does this connection stand in for that RudderStack account?
 *
 * Only a connection with NO account at all (detached) can, and only at the same type, host and
 * database. Such a connection and the account it used to read from would otherwise be listed twice —
 * once as Needs attention, once as Available — and the Available row's Connect would be refused
 * anyway, because a connection already exists at those coordinates. The connection row is the one
 * kept: it is what dashboards and routines reference, and its Connect offers this account.
 *
 * The database is compared only when the account names one. The overview leaves it blank where it
 * cannot be read (Databricks catalogs, Redshift), and then the host alone decides. Known gap
 * (ACT2-1077): two catalogs on one host are two connection keys, so a detached connection on one of
 * them also hides an unconnected account for the other. The exact fix needs the server to send each
 * account's would-be connection key in `listRsAccountsOverview`, so this can compare keys.
 */
export function detachedConnectionCovers(account: WarehouseAccount, rsAccount: RsAccountOverview): boolean {
  if (account.credentialSource !== 'platform' || account.rsAccountId) return false;
  return sameWarehouse(account, rsAccount);
}

/** The connection is at the account's type, host and database (the database only when the account names one). */
function sameWarehouse(
  account: WarehouseAccount,
  target: { type: string; host: string | null; database: string | null },
): boolean {
  const [type = '', host = '', db = ''] = account.accountKey.split('|');
  if (canonicalWarehouseTypeKey(type) !== canonicalWarehouseTypeKey(target.type)) return false;
  if ((target.host ?? '').toLowerCase() !== host.toLowerCase()) return false;
  const targetDb = (target.database ?? '').toLowerCase();
  return targetDb === '' || targetDb === db.toLowerCase();
}

/**
 * The organization's accounts that this list offers: not one whose copy is already among this workspace's own
 * accounts (that row stands in for it), and not one at a warehouse that a connection already covers, because Connect
 * would copy the account and then be refused (`connection_exists`).
 */
function offeredOrganizationAccounts(
  accounts: readonly WarehouseAccount[],
  overview: readonly RsAccountOverview[],
  organization: readonly OrganizationAccountOption[],
): (OrganizationAccountOption & { warehouse: NonNullable<OrganizationAccountOption['warehouse']> })[] {
  const own = new Set(overview.map((a) => a.accountId));
  return organization.flatMap((option) => {
    const { warehouse } = option;
    if (warehouse === null) return [];
    if (option.copyAccountId !== null && own.has(option.copyAccountId)) return [];
    if (accounts.some((connection) => sameWarehouse(connection, warehouse))) return [];
    return [{ ...option, warehouse }];
  });
}

/** The RudderStack accounts no connection holds or stands in for. */
function unheldRsAccounts(
  accounts: readonly WarehouseAccount[],
  overview: readonly RsAccountOverview[],
): RsAccountOverview[] {
  return overview.filter((a) => a.connectionKey === null && !accounts.some((c) => detachedConnectionCovers(c, a)));
}

/**
 * The RudderStack accounts one click of Connect can turn into a connection: this page's Available rows,
 * and the list chat offers when a question arrives with no warehouse. Both read this one rule.
 */
export function connectableRsAccounts(
  accounts: readonly WarehouseAccount[],
  overview: readonly RsAccountOverview[],
): { connectable: RsAccountOverview[]; unconnectable: RsAccountOverview[] } {
  const unheld = unheldRsAccounts(accounts, overview);
  return { connectable: unheld.filter((a) => a.connectable), unconnectable: unheld.filter((a) => !a.connectable) };
}

/**
 * Every connection, then every RudderStack account no connection holds or stands in for, then (in a sibling
 * workspace) the accounts of the organization's other workspaces that this list offers.
 */
export function buildWarehouseRows(
  accounts: readonly WarehouseAccount[],
  overview: readonly RsAccountOverview[],
  organization: readonly OrganizationAccountOption[] = [],
): WarehouseListRow[] {
  const connections: ConnectionRow[] = accounts.map((account) => {
    const bound = boundRsAccount(account, overview);
    const health = connectionHealth(account, bound);
    return {
      kind: 'connection',
      id: account.id,
      status: health.healthy ? 'connected' : 'needs_attention',
      name: account.name,
      type: account.type,
      typeLabel: warehouseTypeMeta(account.type).label,
      detail: connectionCoordinates(account.accountKey) || null,
      account,
      health,
      orientation: orientationIndicator(account.orientation),
      ownedByRudderStack: ownedByRudderStack(bound),
      bound,
    };
  });
  const { connectable, unconnectable } = connectableRsAccounts(accounts, overview);
  const rsAccounts: RsAccountRow[] = [
    ...connectable.map((a) => [a, 'available'] as const),
    ...unconnectable.map((a) => [a, 'cant_connect'] as const),
  ].map(([rsAccount, status]) => {
    const { title, detail } = accountRowIdentity(rsAccount);
    return {
      kind: 'rs_account',
      id: rsAccount.accountId,
      status,
      name: title,
      type: rsAccount.type,
      typeLabel: warehouseTypeMeta(rsAccount.type).label,
      detail,
      rsAccount,
    };
  });
  const organizationRows: OrganizationAccountRow[] = offeredOrganizationAccounts(accounts, overview, organization).map(
    (option) => {
      const { title, detail } = accountRowIdentity({ ...option.warehouse, name: option.name });
      return {
        kind: 'org_account',
        id: `organization:${option.accountId}`,
        status: option.connectable ? 'available' : 'cant_connect',
        name: title,
        type: option.warehouse.type,
        typeLabel: warehouseTypeMeta(option.warehouse.type).label,
        detail: [detail, `From ${option.workspaceName}`].filter((part) => part !== null).join(' · '),
        option,
      };
    },
  );
  return [...connections, ...rsAccounts, ...organizationRows];
}

/** A row that one click of Connect turns into a connection: an own RudderStack account, or one of a readable workspace. */
export type ConnectableWarehouseRow = RsAccountRow | OrganizationAccountRow;

/**
 * The rows that the setup panel's warehouse step and chat's connect card list: the Available rows of Settings ›
 * Warehouses, from the same `buildWarehouseRows`, so the three lists cannot differ. Own accounts come first.
 * `unconnectableCount` counts the accounts that Lookout cannot connect (Can't connect), which are not listed.
 */
export function connectableWarehouseRows(rows: readonly WarehouseListRow[]): {
  connectable: ConnectableWarehouseRow[];
  unconnectableCount: number;
} {
  const accounts = rows.filter((row): row is ConnectableWarehouseRow => row.kind !== 'connection');
  return {
    connectable: accounts.filter((row) => row.status === 'available'),
    unconnectableCount: accounts.filter((row) => row.status === 'cant_connect').length,
  };
}

export type WarehouseSortKey = 'status' | 'name' | 'type';
export type WarehouseFilterKey = 'status';

export const WAREHOUSE_SORT_KEYS: readonly WarehouseSortKey[] = ['status', 'name', 'type'];
export const WAREHOUSE_FILTER_KEYS: readonly WarehouseFilterKey[] = ['status'];
export const WAREHOUSE_STATUSES: readonly WarehouseStatus[] = [
  'connected',
  'needs_attention',
  'available',
  'cant_connect',
];

export const WAREHOUSE_DEFAULT_SORT: SortState<WarehouseSortKey> = { key: 'status', dir: 'asc' };

export const WAREHOUSE_SORT_OPTIONS = [
  { value: 'status', label: 'Status' },
  { value: 'name', label: 'Name' },
  { value: 'type', label: 'Type' },
] as const;

/** Ticked when the Status filter is untouched: everything except the accounts that can't be connected. */
export const WAREHOUSE_DEFAULT_STATUSES: readonly WarehouseStatus[] = ['connected', 'needs_attention', 'available'];

/**
 * The Status filter: a checkbox per status, with its count, Can't connect unticked by default. Every
 * status is listed, even at zero, so the boxes say exactly what is shown. Omitted only when there is
 * nothing it could change — every row in one default status.
 */
export function warehouseFilterGroups(rows: readonly WarehouseListRow[]): FilterGroup[] {
  const present = new Set(rows.map((r) => r.status));
  const onlyDefaults = [...present].every((s) => WAREHOUSE_DEFAULT_STATUSES.includes(s));
  if (present.size <= 1 && onlyDefaults) return [];
  const options = WAREHOUSE_STATUSES.map((status) => ({
    value: status,
    label: `${WAREHOUSE_STATUS_LABEL[status]} (${rows.filter((r) => r.status === status).length})`,
  }));
  return [{ key: 'status', label: 'Status', options, defaultValues: WAREHOUSE_DEFAULT_STATUSES }];
}

/** The statuses the Status filter has ticked — its defaults when untouched. */
function selectedStatuses(statusFilter: string | null | undefined): string[] {
  return parseMultiFilter(statusFilter, WAREHOUSE_DEFAULT_STATUSES);
}

/**
 * The rows the Status filter lets through. Fed to `filterSortItems` and to the toolbar's count, so
 * "N warehouse accounts" counts what is listed.
 */
export function listedWarehouseRows(
  rows: readonly WarehouseListRow[],
  statusFilter: string | null | undefined,
): WarehouseListRow[] {
  const selected = selectedStatuses(statusFilter);
  return rows.filter((r) => selected.includes(r.status));
}

/** Ascending; `filterSortItems` applies the direction. Ties end on the id so refetches never reshuffle. */
const byName = (a: WarehouseListRow, b: WarehouseListRow): number =>
  a.name.toLowerCase().localeCompare(b.name.toLowerCase(), 'en') || a.id.localeCompare(b.id, 'en');

export const WAREHOUSE_DERIVE_CONFIG: ListDeriveConfig<WarehouseListRow, WarehouseSortKey, WarehouseFilterKey> = {
  searchText: (r) => [r.name, r.typeLabel, r.detail ?? '', WAREHOUSE_STATUS_LABEL[r.status]],
  comparators: {
    status: (a, b) => STATUS_RANK[a.status] - STATUS_RANK[b.status] || byName(a, b),
    name: byName,
    type: (a, b) => a.typeLabel.localeCompare(b.typeLabel, 'en') || byName(a, b),
  },
  filterPredicates: {
    status: (r, value) => selectedStatuses(value).includes(r.status),
  },
};
